import { useApplianceDetails } from '@hakimo-ui/hakimo/data-access';
import { Appliance } from '@hakimo-ui/hakimo/types';
import { BasicPanel, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { toast, withAuthz } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { dash, formatDate, formatDateTime, humanizeType } from './util';

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
        {props.label}
      </dt>
      <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
        {props.children}
      </dd>
    </div>
  );
}

function SshCommand({ appliance }: { appliance: Appliance }) {
  if (!appliance.frpPort) {
    return (
      <p className="dark:text-ondark-text-3 text-onlight-text-3 text-sm">
        No FRP port configured
      </p>
    );
  }
  const command = `ssh -p ${appliance.frpPort} ${
    appliance.username || 'hakimo'
  }@frp.hakimo.ai`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast('SSH command copied to clipboard', { type: 'success' });
    } catch {
      toast('Failed to copy to clipboard', { type: 'error' });
    }
  };
  return (
    <div className="dark:bg-ondark-bg-3 bg-onlight-bg-3 col-span-2 flex items-center justify-between gap-2 rounded p-2">
      <code className="dark:text-ondark-text-1 text-onlight-text-1 text-xs">
        {command}
      </code>
      <Button variant="icon" onClick={onCopy} title="Copy to clipboard">
        <ClipboardIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ApplianceDetailsBase() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { key } = useLocation();
  const query = useApplianceDetails(id);

  const onClickBack = () =>
    key === 'default' ? navigate('/appliances') : navigate(-1);

  return (
    <Page title="Appliance" titleFontSize="large" onClickBack={onClickBack}>
      <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full justify-center py-6">
        <div className="w-full max-w-6xl space-y-6 px-4">
          <QueryResult queryResult={query}>
            {(a) => (
              <div className="space-y-6">
                <BasicPanel title="Identity">
                  <dl className="grid grid-cols-2 gap-4 px-6 py-4">
                    <Field label="Name">{a.applianceName}</Field>
                    <Field label="ID">{dash(a.applianceId)}</Field>
                    <Field label="Type">{humanizeType(a.applianceType)}</Field>
                    <Field label="Serial">{dash(a.serialNumber)}</Field>
                    <Field label="MAC address">{dash(a.macAddress)}</Field>
                    <Field label="OS version">{dash(a.osVersion)}</Field>
                  </dl>
                </BasicPanel>
                <BasicPanel title="Connectivity">
                  <dl className="grid grid-cols-2 gap-4 px-6 py-4">
                    <Field label="VPN IP">{dash(a.openvpnIp)}</Field>
                    <Field label="FRP port">{dash(a.frpPort)}</Field>
                    <Field label="Username">{dash(a.username)}</Field>
                    <Field label="New Relic host">
                      {dash(a.newrelicHostname)}
                    </Field>
                    <Field label="Teamviewer host">
                      {dash(a.teamviewerHostname)}
                    </Field>
                    <SshCommand appliance={a} />
                  </dl>
                </BasicPanel>
                <BasicPanel title="Assignment">
                  <dl className="grid grid-cols-2 gap-4 px-6 py-4">
                    <Field label="Tenant">
                      {a.tenantId && a.tenantName ? (
                        <Link
                          to={`/tenants-admin/${a.tenantId}`}
                          className="text-primary-500 hover:underline"
                        >
                          {a.tenantName}
                        </Link>
                      ) : (
                        dash(a.tenantName)
                      )}
                    </Field>
                    <Field label="Location">
                      {dash(a.physicalLocationName)}
                    </Field>
                  </dl>
                </BasicPanel>
                <BasicPanel title="Lifecycle">
                  <dl className="grid grid-cols-2 gap-4 px-6 py-4">
                    <Field label="Status">{dash(a.hostStatus)}</Field>
                    <Field label="Enabled">
                      {a.isEnabled === null ? '—' : a.isEnabled ? 'Yes' : 'No'}
                    </Field>
                    <Field label="Ship to customer">
                      {formatDate(a.shipDateToCustomer)}
                    </Field>
                    <Field label="Ship to Hakimo">
                      {formatDate(a.shipDateToHakimo)}
                    </Field>
                    <Field label="Last seen">
                      {formatDateTime(a.lastSeenAt)}
                    </Field>
                    <Field label="Created">{formatDateTime(a.createdAt)}</Field>
                    <Field label="Updated">{formatDateTime(a.updatedAt)}</Field>
                  </dl>
                </BasicPanel>
              </div>
            )}
          </QueryResult>
        </div>
      </div>
    </Page>
  );
}

export const ApplianceDetails = withAuthz(ApplianceDetailsBase, [
  'appliances:view',
]);

export default ApplianceDetails;
